import { toRenderSlot, type AppState } from '../../state/appState.js';
import type { EditorCommands } from '../../editor/commands.js';
import type { SceneNodeId } from '../../scene/sceneState.js';
import type { NodeEditorController } from '../nodeEditor.js';
import { getActiveContainerId } from './queries.js';
import type { SceneTreeSelectionController } from './selection.js';

export type SceneTreeFrameLayout = 'row' | 'column' | 'stack' | null;

export interface SceneTreeActionDeps {
  appState: AppState;
  commands: Pick<
    EditorCommands,
    'addFrame' | 'addLayer' | 'addInstance' | 'addExistingInstance' | 'deleteSceneNode'
  >;
  loadComponentSlot(componentId: string | null, slot: 'geometry' | 'material', layerId?: string): boolean;
  nodeEditor: Pick<NodeEditorController, 'renderGraph'>;
  selection: SceneTreeSelectionController;
  renderEditorNavigation(): void;
  renderSceneTree(): void;
  scheduleRender(): void;
  syncEditor(): boolean;
  updateParams(): void;
}

function getActionActiveScene(appState: AppState) {
  if (appState.activeComponentId) {
    return appState.componentLibrary[appState.activeComponentId]?.scene ?? appState.scene;
  }
  return appState.scene;
}

export function switchToComponent(deps: SceneTreeActionDeps, componentId: string): void {
  const { appState, loadComponentSlot, nodeEditor, scheduleRender, syncEditor, updateParams } = deps;
  const wasActive = componentId === appState.activeComponentId;
  const component = appState.componentLibrary[componentId];
  if (!component) return;

  if (!wasActive) {
    syncEditor();
    const loaded = loadComponentSlot(componentId, toRenderSlot(appState.editorGraphSlot));
    if (!loaded) {
      appState.activeComponentId = componentId;
      appState.editorContext = {
        mode: 'layer',
        componentId,
        layerId: undefined,
        layerSlot: toRenderSlot(appState.editorGraphSlot),
      };
      appState.nodes = [];
      appState.edges = [];
      appState.editorGraphDirty = false;
      appState.selectedNode = null;
    }
  }

  deps.selection.selectVirtualTarget('component');

  nodeEditor.renderGraph();
  updateParams();
  scheduleRender();
  deps.renderSceneTree();
  deps.renderEditorNavigation();
}

export function returnToScene(deps: SceneTreeActionDeps): void {
  const { appState, nodeEditor, scheduleRender, syncEditor, updateParams } = deps;
  syncEditor();
  appState.activeComponentId = null;
  deps.selection.selectVirtualTarget('scene');
  nodeEditor.renderGraph();
  updateParams();
  deps.renderSceneTree();
  deps.renderEditorNavigation();
  scheduleRender();
}

export function addFrameWithLayout(deps: SceneTreeActionDeps, layout: SceneTreeFrameLayout): void {
  const { appState, commands, scheduleRender } = deps;
  const activeScene = getActionActiveScene(appState);
  const parentId = getActiveContainerId(activeScene, deps.selection.getSelectedSceneNodeId());
  const result = commands.addFrame(layout, parentId);
  deps.selection.selectSceneNode(result.id);
  deps.renderSceneTree();
  scheduleRender();
}

export function addLayerNode(deps: SceneTreeActionDeps): void {
  const { appState, commands, scheduleRender } = deps;
  const activeScene = getActionActiveScene(appState);
  const parentId = getActiveContainerId(activeScene, deps.selection.getSelectedSceneNodeId());
  const result = commands.addLayer(parentId);
  if (!result) return;
  deps.selection.selectSceneNode(result.nodeId);
  deps.renderSceneTree();
  scheduleRender();
}

export function addBlankInstance(deps: SceneTreeActionDeps): void {
  const { appState, commands, scheduleRender } = deps;
  const activeScene = getActionActiveScene(appState);
  const parentId = getActiveContainerId(activeScene, deps.selection.getSelectedSceneNodeId());
  const result = commands.addInstance(parentId);
  deps.selection.selectSceneNode(result.instanceId as SceneNodeId);
  deps.renderSceneTree();
  scheduleRender();
}

export function addExistingComponentInstance(deps: SceneTreeActionDeps, componentId: string): void {
  const { appState, commands, scheduleRender } = deps;
  const activeScene = getActionActiveScene(appState);
  const parentId = getActiveContainerId(activeScene, deps.selection.getSelectedSceneNodeId());
  const result = commands.addExistingInstance(componentId, parentId);
  if (!result) return;
  deps.selection.selectSceneNode(result.instanceId as SceneNodeId);
  deps.renderSceneTree();
  scheduleRender();
}

export function deleteSelectedNode(deps: SceneTreeActionDeps): void {
  const { commands, nodeEditor, scheduleRender, updateParams } = deps;
  const selectedSceneNodeId = deps.selection.getSelectedSceneNodeId();
  if (!selectedSceneNodeId) return;

  commands.deleteSceneNode(selectedSceneNodeId);
  deps.selection.setSelectedSceneNodeId(null);
  deps.selection.setSelectedVirtualTarget(null);

  nodeEditor.renderGraph();
  updateParams();
  deps.renderSceneTree();
  scheduleRender();
}
